import { useCallback, useContext, useEffect, useState } from "react";

import { Button, InfoBar, Menu, Modal } from "@shapr3d/frontend-lib";
import { useParams } from "react-router-dom";

import { DBAvailableContext } from "../../contexts/DBAvailableProvider";
import useNavigation from "../../hooks/navigation";
import useAccount, {
  useCancelSubscription,
  useMutatePaddleAutoRenew,
} from "../../hooks/useAccount";
import type { Subscription } from "../../hooks/useAccountTypes";
import { useTeam } from "../../hooks/useTeams";
import { useTracker } from "../../hooks/useTracker";
import {
  ElementClickAction,
  ElementClickLocation,
  ModalShownAction,
} from "../../lib/TrackingAdapter";
import { SubscriptionType } from "../../types";
import CTARow from "../CTARow/CTARow";
import { CTARowDivider } from "../CTARowDivider/CTARowDivider";
import { GenericErrorModal } from "../Modal/Modal";
import { SubscriptionCancelModal } from "../TeamSubscriptions/SubscriptionCancelModal/SubscriptionCancelModal";
import { SubscriptionExpirationText } from "../TeamSubscriptions/SubscriptionExpirationText/SubscriptionExpirationText";
import { getPlanDisplayName, getSourceDisplayName } from "../TeamSubscriptions/subscriptionUtils";

import style from "./SubscriptionInfo.module.css";

const openEduMoreInformation = () => {
  window.open(
    "https://support.shapr3d.com/hc/en-us/articles/115001796629",
    "_blank",
    "noopener,noreferrer"
  );
};

const openPricingPage = () => {
  window.open("https://shapr3d.com/pricing", "_blank", "noopener,noreferrer");
};

export default function SubscriptionInfo({
  subscription,
  hasAppStoreSubscription,
  techTrialAvailable,
  handlePaymentInfoUpdate,
}: {
  subscription: Subscription;
  hasAppStoreSubscription: boolean;
  techTrialAvailable: boolean;
  handlePaymentInfoUpdate: (subscription: Subscription) => void;
}) {
  const { data: accountData } = useAccount();

  const tracker = useTracker();
  const navigate = useNavigation();
  const { isDBDown } = useContext(DBAvailableContext);
  const [isAutoRenewCancelModalOpen, setIsAutoRenewCancelModalOpen] = useState(false);
  const { type, tier, subscriptionPeriod, expiration } = subscription;
  const navigation = useNavigation();

  const { teamID } = useParams();
  const { teamData } = useTeam(teamID);

  const planName = getPlanDisplayName(type, subscriptionPeriod, tier, subscription.teamName);

  const expirationDate = new Date(expiration).toLocaleDateString("en-us", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const purchaseSource = getSourceDisplayName(type);
  const { mutate: mutatePaddleAutoRenew, isPending: isPaddleAutoRenewPending } =
    useMutatePaddleAutoRenew({ teamID });
  const { mutate: mutateCancelSubscription, isPending: isCancelPending } = useCancelSubscription();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isSeatManagementModalOpen, setIsSeatManagementModalOpen] = useState(false);

  const shouldDisplayAutoRenewOnButton =
    subscription.isAutoRenewEditable && !subscription.autorenewal;
  const shouldDisplaySubscriptionCancelButton =
    subscription.autorenewal && (subscription.isAutoRenewEditable || subscription.isOnlyCancelable);

  const isAutoRenewLoading = isPaddleAutoRenewPending;

  const handlePaddleAutoRenewChange = useCallback(
    (autoRenew: boolean) => {
      const paddleSubscriptionID = subscription.paddleId ?? "";
      if (subscription.isAutoRenewEditable) {
        mutatePaddleAutoRenew(
          {
            autoRenew,
            paddleSubscriptionID,
          },
          {
            onError: () => setIsErrorModalOpen(true),
            onSettled: () => {
              setIsAutoRenewCancelModalOpen(false);
            },
          }
        );
      } else if (subscription.isOnlyCancelable) {
        mutateCancelSubscription(
          { paddleSubscriptionID },
          {
            onError: () => setIsErrorModalOpen(true),
            onSettled: () => {
              setIsAutoRenewCancelModalOpen(false);
            },
          }
        );
      }
    },
    [mutatePaddleAutoRenew, mutateCancelSubscription, subscription]
  );

  const handleAutoRenewChange = useCallback(
    (autoRenew: boolean) => {
      if (subscription.type === SubscriptionType.Paddle) {
        handlePaddleAutoRenewChange(autoRenew);
        return;
      }

      throw new Error("Unsupported subscription type");
    },
    [subscription, handlePaddleAutoRenewChange]
  );

  const isInGracePeriod = useCallback(() => {
    return subscription.gracePeriodExpirationDate !== undefined;
  }, [subscription]);

  const moreInformation = subscription.type === SubscriptionType.Edu;
  const isSubscriptionManagable =
    shouldDisplaySubscriptionCancelButton ||
    techTrialAvailable ||
    subscription.autorenewal ||
    moreInformation;

  const isSubscriptionMigrationEnabled =
    !isInGracePeriod() &&
    (subscription.type === SubscriptionType.Paddle ||
      subscription.type === SubscriptionType.AppStore) &&
    accountData?.availablePlans?.length;

  useEffect(() => {
    if (isSeatManagementModalOpen) {
      tracker.trackModalShown(ModalShownAction.ADD_SEAT_UNAVAILABLE, "individual_subscription");
    }
  }, [isSeatManagementModalOpen, tracker]);

  const handleAddSeats = () => {
    tracker.trackButtonClick({ action: ElementClickAction.ADD_SEATS });
    if (isSubscriptionMigrationEnabled) {
      navigate.goToCheckout({
        selectedTier: subscription.tier,
        period: subscription.subscriptionPeriod,
        fromAddSeats: true,
      });
    } else {
      setIsSeatManagementModalOpen(true);
    }
  };

  return (
    <div>
      <CTARow title="Subscription" text={planName} />
      <CTARowDivider />
      <CTARow
        title="Seats"
        text="1"
        CTA={
          <Menu
            activator={
              <Button
                variant="borderless"
                onClick={() => {
                  tracker.trackButtonClick({
                    action: ElementClickAction.ADD_SEATS,
                    location: ElementClickLocation.MANAGE_SEATS_MENU,
                  });
                }}
              >
                Manage
              </Button>
            }
          >
            <Menu.Item onClick={handleAddSeats} closeOnClick>
              Add seats
            </Menu.Item>
            <Menu.Item disabled>Manage team size</Menu.Item>
          </Menu>
        }
      />
      <CTARowDivider />
      <CTARow
        title={
          <SubscriptionExpirationText
            autoRenews={subscription.autorenewal}
            subscriptionType={type}
            expirationDate={expiration}
            hasAppStoreSubscription={hasAppStoreSubscription}
          />
        }
        text={expirationDate}
        notification={
          shouldDisplayAutoRenewOnButton && (
            <InfoBar
              text="Turn on auto-renewal to prevent losing access to Pro features at the end of the billing cycle."
              variant="attention"
              cta={
                <Button
                  variant="grayscale"
                  onClick={() => {
                    handleAutoRenewChange(true);
                    tracker.trackButtonClick({ action: ElementClickAction.TURN_ON_AUTO_RENEWAL });
                  }}
                  disabled={isAutoRenewLoading}
                >
                  Turn on auto-renewal
                </Button>
              }
            ></InfoBar>
          )
        }
        CTA={
          !isDBDown &&
          isSubscriptionManagable && (
            <Menu
              activator={
                <Button
                  variant="borderless"
                  style={{ height: "100%" }}
                  onClick={() => {
                    tracker.trackButtonClick({ action: ElementClickAction.MANAGE_AUTO_RENEWAL });
                  }}
                >
                  Manage
                </Button>
              }
            >
              {shouldDisplaySubscriptionCancelButton && (
                <Menu.Item
                  onClick={() => {
                    setIsAutoRenewCancelModalOpen(true);
                    tracker.trackButtonClick({
                      action: ElementClickAction.CANCEL_SUBSCRIPTION,
                      location: ElementClickLocation.AUTO_RENEWAL_MANAGE_MENU,
                    });
                  }}
                  disabled={isAutoRenewLoading}
                  closeOnClick
                >
                  <div style={{ color: "var(--control-error-label)" }}>Cancel subscription</div>
                </Menu.Item>
              )}
              {techTrialAvailable && (
                <Menu.Item onClick={openPricingPage} closeOnClick>
                  Change to a direct Shapr3D plan
                </Menu.Item>
              )}
              {subscription.autorenewal && (
                <Menu.Item
                  onClick={() => {
                    tracker.trackButtonClick({
                      action: ElementClickAction.UPDATE_PAYMENT_INFORMATION,
                      location: ElementClickLocation.AUTO_RENEWAL_MANAGE_MENU,
                    });
                    return handlePaymentInfoUpdate(subscription);
                  }}
                  closeOnClick
                >
                  Update payment information
                </Menu.Item>
              )}
              {moreInformation && (
                <Menu.Item onClick={openEduMoreInformation} closeOnClick>
                  How to renew my education license?
                </Menu.Item>
              )}
            </Menu>
          )
        }
      />
      <CTARowDivider />
      <CTARow title="Purchased via" text={purchaseSource} />

      <Modal
        open={isSeatManagementModalOpen}
        title="Add Seats"
        size="small"
        onClose={() => setIsSeatManagementModalOpen(false)}
      >
        <div className={style.notAvailableModal}>
          <span>Adding seats to {teamData?.name} is unavailable.</span>
          <div className={style.message}>
            <span>Learn more how to start collaborating with your team.</span>
          </div>
          <Button onClick={navigation.openTeamsArticle}>Learn more</Button>
        </div>
      </Modal>

      <SubscriptionCancelModal
        open={isAutoRenewCancelModalOpen}
        onClose={() => setIsAutoRenewCancelModalOpen(false)}
        editorSeatCount={1}
        isSubscriptionInGracePeriod={isInGracePeriod()}
        subscriptionExpirationDate={expirationDate}
        onCancelSubscription={() => {
          handleAutoRenewChange(false);
        }}
        isLoading={isAutoRenewLoading || isCancelPending}
      />

      <GenericErrorModal open={isErrorModalOpen} onClose={() => setIsErrorModalOpen(false)} />
    </div>
  );
}
